/**
 * Azure Functions accepts a function key either as a `?code=` query parameter or as the
 * `x-functions-key` request header. We always use the header.
 *
 * A key in the URL is copied into browser history, server and proxy access logs, and `Referer`
 * headers, and URLs carrying token-like values also match ad-blocker filter rules — which cancels
 * the request outright (ERR_BLOCKED_BY_CLIENT) instead of failing loudly.
 *
 * Using a custom header makes the request "non-simple", so the browser sends a CORS preflight
 * first. That is allowed: the Function App answers the preflight with
 * `Access-Control-Allow-Headers: x-functions-key`, and the host accepts a key sent this way.
 *
 * @param {string} key the function key (from a VUE_APP_FUNCTION_*_KEY env var)
 * @returns {{ 'x-functions-key': string }} headers to spread into an axios/fetch request
 */
export function functionKeyHeaders(key) {
  return { 'x-functions-key': key }
}

/**
 * Builds a fire-and-forget POST that must go out while the page is being torn down (a
 * `beforeunload` handler, or a logout that navigates away).
 *
 * Such a request gets ONE chance, so it must be a CORS "simple" request: no custom headers and no
 * explicit `Content-Type` (fetch then sends `text/plain;charset=UTF-8`, which is allowed). The
 * browser sends a simple request immediately, with no `OPTIONS` preflight — and even if the server
 * does not return CORS approval, the request still REACHES the server; only the response is hidden
 * from us. We do not read the response, so that is fine. A preflight would mean two sequential
 * round trips, and the second one often never leaves.
 *
 * The cost: a simple request cannot carry `x-functions-key`, so the key goes in `?code=` for this
 * one call — a deliberate, narrow exception to the rule above. The caller's identity stays in the
 * body, so no personal data is in the URL.
 *
 * NOTE for the receiving Function: read the body as raw text and parse it yourself. Do not rely on
 * `Content-Type: application/json`.
 *
 * @param {string} url the endpoint, which may already carry a query string
 * @param {string} key the function key (from a VUE_APP_FUNCTION_*_KEY env var)
 * @param {string} body the request body, already serialized
 * @returns {{ url: string, init: RequestInit }} arguments to pass straight to `fetch`
 */
export function unloadSafePost(url, key, body) {
  const separator = url.includes('?') ? '&' : '?'

  return {
    url: `${url}${separator}code=${encodeURIComponent(key)}`,
    init: {
      method: 'POST',
      body,
      // Lets the request outlive the page, like sendBeacon did — but without sendBeacon's
      // downside: beacons are the browser's tracking API, so ad blockers and Brave Shields
      // reject them outright (ERR_BLOCKED_BY_CLIENT) and the agent silently stays available.
      keepalive: true,
    },
  }
}
